import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, ChevronDown, UserPlus, Users, Sparkles } from 'lucide-react';
import { eventsService } from '../../../../../../services';
import './ShooterSelector.css';

const ShooterSelector = ({ selectedShooter, currentShooters = [], onShooterSelect }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [availableShooters, setAvailableShooters] = useState([]);
    const [searchResults, setSearchResults] = useState([]);
    const [guestName, setGuestName] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const dropdownRef = useRef(null);

    const selectedKeys = new Set(
        currentShooters.map((shooter) => shooter.appUserId || `guest-${(shooter.shooterName || '').toLowerCase()}`)
    );

    const currentUserOption = availableShooters.find((shooter) => Boolean(shooter.appUserId));
    const selectableCurrentUser = currentUserOption && !selectedKeys.has(currentUserOption.appUserId) ? currentUserOption : null;
    const friendOptions = availableShooters.filter((shooter) => {
        if (!shooter.appUserId) return false;
        if (currentUserOption?.appUserId === shooter.appUserId) return false;
        return !selectedKeys.has(shooter.appUserId);
    });

    const fetchAvailableShooters = useCallback(async () => {
        try {
            setIsLoading(true);
            const data = await eventsService.getAvailableShooters();
            setAvailableShooters(data);
            setSearchResults(data);
        } catch (error) {
            console.error('Error fetching available shooters:', error);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchAvailableShooters();

        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [fetchAvailableShooters]);

    const handleSearch = useCallback(() => {
        if (!searchTerm.trim()) {
            setSearchResults(friendOptions);
            return;
        }

        const results = friendOptions.filter(shooter =>
            `${shooter.firstName || ''} ${shooter.lastName || ''} ${shooter.shooterName || ''} ${shooter.userName || ''}`
                .toLowerCase()
                .includes(searchTerm.toLowerCase())
        );
        setSearchResults(results);
    }, [searchTerm, friendOptions]);

    useEffect(() => {
        handleSearch();
    }, [searchTerm, handleSearch]);

    const handleGuestSubmit = (e) => {
        e.preventDefault();
        if (!guestName.trim()) return;

        onShooterSelect({
            appUserId: null,
            shooterName: guestName.trim()
        });
        setGuestName('');
        setIsOpen(false);
    };

    const getName = (shooter) => {
        if (shooter.shooterName) return shooter.shooterName;
        if (shooter.firstName || shooter.lastName) return `${shooter.firstName || ''} ${shooter.lastName || ''}`.trim();
        return 'Unknown Shooter';
    };

    const getInitials = (name) => {
        if (!name) return 'SC';
        const parts = name.split(' ').filter(Boolean).slice(0, 2);
        return parts.map((part) => part[0]?.toUpperCase()).join('') || 'SC';
    };

    const renderAvatar = (shooter, className = 'shooter-selector-avatar') => {
        const displayName = getName(shooter);
        if (shooter.profilePictureUrl) {
            return (
                <img
                    src={shooter.profilePictureUrl}
                    alt={displayName}
                    className={`${className} shooter-selector-avatar-image`}
                />
            );
        }

        return <span className={className}>{getInitials(displayName)}</span>;
    };

    const handleSelectShooter = (shooter) => {
        onShooterSelect(shooter);
        setSearchTerm('');
        setIsOpen(false);
    };

    return (
        <div className="dropdown create-event-dropdown" ref={dropdownRef}>
            <button
                type="button"
                className="shooter-selector-button"
                onClick={() => setIsOpen(!isOpen)}
                aria-expanded={isOpen}
            >
                <span className="shooter-selector-text">
                    {selectedShooter ? getName(selectedShooter) : 'Add shooters to this event'}
                </span>
                <ChevronDown className={`w-5 h-5 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {isOpen && (
                <div className="shooter-selector-dropdown">
                    <div className="shooter-selector-panel">
                        <div className="shooter-selector-intro">
                            <h5 className="shooter-selector-heading">Add shooters</h5>
                            <p className="shooter-selector-meta">
                                Start with yourself, then bring in friends or add a guest for the day.
                            </p>
                        </div>

                        <div className="shooter-selector-layout">
                            <section className="shooter-selector-block shooter-selector-block-accent">
                                <div className="shooter-selector-block-header">
                                    <div>
                                        <span className="shooter-selector-kicker">You</span>
                                        <h6>Add yourself to shoot</h6>
                                    </div>
                                    <Sparkles size={16} />
                                </div>
                                {selectableCurrentUser ? (
                                    <button
                                        type="button"
                                        className="shooter-selector-self-btn"
                                        onClick={() => handleSelectShooter(selectableCurrentUser)}
                                    >
                                        {renderAvatar(selectableCurrentUser)}
                                        <span className="shooter-selector-self-copy">
                                            <strong>{getName(selectableCurrentUser)}</strong>
                                            <small>Add yourself as a shooter on this event</small>
                                        </span>
                                        <UserPlus size={16} />
                                    </button>
                                ) : (
                                    <p className="shooter-selector-inline-empty">You are already on this event.</p>
                                )}
                            </section>

                            <section className="shooter-selector-block">
                                <div className="shooter-selector-block-header">
                                    <div>
                                        <span className="shooter-selector-kicker">Friends</span>
                                        <h6>Choose from connections</h6>
                                    </div>
                                    <Users size={16} />
                                </div>
                                <div className="shooter-selector-search">
                                    <Search className="w-4 h-4" />
                                    <input
                                        type="text"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        placeholder="Search friends by name"
                                        className="w-full bg-transparent border-none outline-none"
                                    />
                                </div>

                                <div className="shooter-selector-scroll">
                                    {isLoading ? (
                                        <p className="shooter-selector-empty">Loading shooters...</p>
                                    ) : (
                                        <>
                                            <div className='shooter-selector-results'>
                                                {searchResults.map((shooter) => {
                                                    const displayName = getName(shooter);
                                                    return (
                                                        <button
                                                            key={shooter.appUserId || `guest-${shooter.shooterName}`}
                                                            type="button"
                                                            className="shooter-selector-result"
                                                            onClick={() => handleSelectShooter(shooter)}
                                                        >
                                                            {renderAvatar(shooter)}
                                                            <span className="shooter-selector-result-copy">
                                                                <span className="shooter-selector-name">{displayName}</span>
                                                                <small>Add from your friends list</small>
                                                            </span>
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                            {searchResults.length === 0 && (
                                                <p className="shooter-selector-empty">
                                                    {friendOptions.length === 0
                                                        ? 'No more friends are available to add right now.'
                                                        : 'No friend matches that search yet.'}
                                                </p>
                                            )}
                                        </>
                                    )}
                                </div>
                            </section>
                        </div>

                        <div className="shooter-selector-guest">
                            <h5 className="shooter-selector-heading">Add a guest shooter</h5>
                            <form onSubmit={handleGuestSubmit}>
                                <div className="shooter-selector-guest-row">
                                    <div className="dashboard-search-field shooter-selector-guest-field">
                                        <input
                                            type="text"
                                            placeholder="Enter guest name exactly as it should appear"
                                            value={guestName}
                                            onChange={(e) => setGuestName(e.target.value)}
                                        />
                                    </div>
                                    <button
                                        className='dashboard-search-submit shooter-selector-guest-submit'
                                        type='submit'
                                        disabled={!guestName.trim()}
                                    >Add</button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ShooterSelector;
